import { Box, useTheme } from '@mui/material'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'
import { subDays, addDays } from 'date-fns'
import { EarningsSummaryDTO } from '../types/EarningsSummary'
import formatCurrency from '../../../utils/formatCurrency'

type Props = {
  summaries: EarningsSummaryDTO[] | undefined
  startDate?: string | null
  endDate?: string | null
}

// Aggregate earnings by day using string date comparisons (efficient - no Date object creation)
const aggregateEarningsByDay = (
  summaries: EarningsSummaryDTO[],
  startDate: string | null,
  endDate: string | null
): { [key: string]: number } => {
  return summaries.reduce(
    (acc, curr) => {
      // Convert Date to string once per summary for comparison
      const dayKey = curr.date.toISOString().slice(0, 10)

      // Direct string comparison (ISO date strings are lexicographically sortable)
      if (startDate && endDate && dayKey >= startDate && dayKey <= endDate) {
        if (!acc[dayKey]) {
          acc[dayKey] = 0
        }
        acc[dayKey] += curr.netEarningsAmount
      }
      return acc
    },
    {} as { [key: string]: number }
  )
}

const getRevenuePerDay = (
  summaries: EarningsSummaryDTO[],
  startDateStr: string | null,
  endDateStr: string | null
) => {
  const revenue = aggregateEarningsByDay(summaries, startDateStr, endDateStr)

  // Step 2: Convert strings to Date objects only for date math operations
  // (needed for: date range calculation, date-fns subDays(), and getTime() timestamps)
  const minDateObj = startDateStr ? new Date(startDateStr + 'T00:00:00') : null
  const maxDateObj = endDateStr ? new Date(endDateStr + 'T00:00:00') : null
  const numberOfDays =
    Math.floor(
      ((maxDateObj?.getTime() || 0) - (minDateObj?.getTime() || 0)) /
        (1000 * 60 * 60 * 24)
    ) + 1

  return Array.from({ length: numberOfDays }, (_, i) => {
    const date = subDays(maxDateObj || new Date(), i)

    return {
      date: date.getTime(),
      total: revenue[date.toISOString().slice(0, 10)] || 0
    }
  })
}

const EarningsChart = ({ summaries, startDate, endDate }: Props) => {
  const theme = useTheme()

  if (!summaries?.length) return <></>

  const validStartDate =
    startDate || summaries[0].date.toISOString().slice(0, 10)
  const validEndDate =
    endDate || summaries[summaries.length - 1].date.toISOString().slice(0, 10)

  // Convert string dates to Date objects for chart domain calculations
  const startDateObj = new Date(validStartDate + 'T00:00:00')
  const endDateObj = new Date(validEndDate + 'T00:00:00')

  return (
    <Box sx={{ width: 'inherit', padding: '10px', height: 300 }}>
      <ResponsiveContainer>
        <AreaChart
          data={getRevenuePerDay(summaries, validStartDate, validEndDate)}
        >
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={theme.palette.primary.main}
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor={theme.palette.primary.main}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            name="Date"
            type="number"
            scale="time"
            domain={[
              startDateObj ? addDays(startDateObj, 1).getTime() : 0,
              endDateObj ? endDateObj.getTime() : 0
            ]}
            tickFormatter={(v) => new Date(v).toLocaleDateString()}
          />
          <YAxis dataKey="total" name="Revenue" unit="$" />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            formatter={formatCurrency}
            labelFormatter={(v) => new Date(v).toLocaleDateString()}
            labelStyle={{ color: theme.palette.primary.main }}
          />
          <Area
            type="monotone"
            dataKey="total"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            fill="url(#colorUv)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default EarningsChart
